import React, { useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { CompareFlowMonitoringDataOutput } from '../model';
import { buildIntraShiftTrend, IntraShiftTrendMetric } from '../utils';

interface IntraShiftTrendChartProps {
    compareResponse: CompareFlowMonitoringDataOutput;
}

const numberFormatter = new Intl.NumberFormat('en-US');
const wholeNumberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// Series colors: Actual = solid blue, Projected = light-blue dashed (flat-pace
// continuation), Plan = orange dashed (target reference). Recharts is styled via
// inline props, so these are passed directly (matching the reviewAndPublish Chart).
const COLORS = {
    actual: '#0073bb',
    projected: '#5aa9e6',
    plan: '#d8730a',
    grid: '#e9ebed',
    tick: '#5f6b7a',
};

const METRIC_TABS: { value: IntraShiftTrendMetric; label: string }[] = [
    { value: 'VOLUME', label: 'Volume' },
    { value: 'HEADCOUNT', label: 'Headcount' },
    { value: 'RATE', label: 'Rate' },
];

const TITLES: Record<IntraShiftTrendMetric, string> = {
    VOLUME: 'Intra-Shift Volume Trend',
    HEADCOUNT: 'Intra-Shift Headcount Trend',
    RATE: 'Intra-Shift Rate Trend',
};

/**
 * Intra-shift trend chart shown between the Maestro cards and the Progress Report
 * table. Plots the same per-hour data the table shows, consolidated per the
 * selected metric tab across DIRECT process paths only:
 *  - Volume    → cumulative VOLUME_PROCESSED across DIRECT paths (each hour is
 *                the running total of all volume up to and including that hour).
 *  - Headcount → summed HEADCOUNT across DIRECT paths (hour to hour).
 *  - Rate      → blended throughput (Σ volume ÷ Σ hours) over DIRECT paths (hour to hour).
 *
 * Three lines: Actual (solid, elapsed hours), Projected (dashed, flat-pace
 * continuation to end of shift), and Plan (dashed, the expected/target values).
 */
const IntraShiftTrendChart: React.FC<IntraShiftTrendChartProps> = ({ compareResponse }) => {
    const [metric, setMetric] = useState<IntraShiftTrendMetric>('VOLUME');

    const data = useMemo(
        () => buildIntraShiftTrend(compareResponse.hourlyShiftComparisonDataList, compareResponse.shiftTimeContext, metric),
        [compareResponse, metric]
    );
    // Volume is always a whole number; Headcount / Rate keep their existing formatting.
    const formatChartValue = (v: number): string =>
        metric === 'VOLUME' ? wholeNumberFormatter.format(Math.round(v)) : numberFormatter.format(v);

    const hasProjected = data.some((d) => d.projected != null);

    return (
        <Box type="outline" spacingInset="400" width="100%">
            <Column spacing="400">
                <Row alignmentHorizontal="justify" alignmentVertical="center" width="100%">
                    <div style={{ whiteSpace: 'nowrap' }}>
                        <Text type="h300">{TITLES[metric]}</Text>
                    </div>
                    {/* TabGroup's root is width:100%, so it fills any container and
                        left-aligns its tabs. Wrapping it in an inline-flex box shrinks
                        it to the tabs' own width so Row's justify can push it right. */}
                    <div style={{ display: 'inline-flex', width: 'auto' }}>
                        <TabGroup
                            value={metric}
                            onChange={(value: string) => setMetric(value as IntraShiftTrendMetric)}
                            fill="none"
                            aria-labelledby="intra-shift-trend-metric-tabs"
                        >
                            {METRIC_TABS.map((tab) => (
                                <Tab key={tab.value} value={tab.value} id={tab.value} aria-controls={tab.value}>
                                    {tab.label}
                                </Tab>
                            ))}
                        </TabGroup>
                    </div>
                </Row>

                <div style={{ width: '100%', height: 320 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                            <CartesianGrid strokeDasharray="1 1" stroke={COLORS.grid} vertical={false} />
                            <XAxis
                                dataKey="hour"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: COLORS.tick }}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                width={56}
                                tick={{ fontSize: 12, fill: COLORS.tick }}
                                tickFormatter={(v: number) => formatChartValue(v)}
                                domain={[0, 'auto']}
                            />
                            <Tooltip formatter={(value: number) => formatChartValue(value)} />

                            {/* Plan — dashed orange target reference, continuous across the shift. */}
                            <Line
                                type="monotone"
                                dataKey="plan"
                                name="Plan"
                                stroke={COLORS.plan}
                                strokeWidth={2}
                                strokeDasharray="6 6"
                                dot={false}
                                connectNulls
                            />

                            {/* Actual — solid blue, elapsed hours only. */}
                            <Line
                                type="monotone"
                                dataKey="actual"
                                name="Actual"
                                stroke={COLORS.actual}
                                strokeWidth={3}
                                dot={{ fill: COLORS.actual, r: 3 }}
                                connectNulls={false}
                            />

                            {/* Projected — dashed light-blue, flat-pace continuation to EOS. */}
                            {hasProjected && (
                                <Line
                                    type="monotone"
                                    dataKey="projected"
                                    name="Projected"
                                    stroke={COLORS.projected}
                                    strokeWidth={2}
                                    strokeDasharray="6 6"
                                    dot={{ fill: COLORS.projected, r: 3, strokeDasharray: '0' }}
                                    connectNulls={false}
                                />
                            )}

                            <Legend verticalAlign="bottom" align="right" iconType="plainline" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Column>
        </Box>
    );
};

export default IntraShiftTrendChart;
